export const WAR_EFFORT_DISMISSED_MISSION_IDS_KEY = 'coda.warEffort.dismissedMissionIds';
export const WAR_EFFORT_MISSION_OVER_MESSAGE_SHOWN_PREFIX = 'coda.warEffort.missionOverMessageShown.';

function requireSessionStorage() {
  if (typeof window === 'undefined' || typeof window.sessionStorage === 'undefined') {
    throw new Error('warEffortDismissedMissions: sessionStorage is required');
  }
  return window.sessionStorage;
}

function parseMissionId(missionId) {
  if (missionId == null || missionId === '') {
    throw new Error('warEffortDismissedMissions: missionId is required');
  }
  const n = Number(missionId);
  if (!Number.isFinite(n)) {
    throw new Error(`warEffortDismissedMissions: invalid missionId ${missionId}`);
  }
  return n;
}

/**
 * @returns {Set<number>}
 */
export function getDismissedWarEffortMissionIds() {
  const storage = requireSessionStorage();
  let raw;
  try {
    raw = storage.getItem(WAR_EFFORT_DISMISSED_MISSION_IDS_KEY);
  } catch (err) {
    throw new Error(`warEffortDismissedMissions: sessionStorage read failed (${err?.message ?? err})`);
  }
  if (raw == null || raw === '') {
    return new Set();
  }
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    throw new Error(`warEffortDismissedMissions: dismissed mission ids JSON is invalid (${err?.message ?? err})`);
  }
  if (!Array.isArray(parsed)) {
    throw new Error('warEffortDismissedMissions: dismissed mission ids must be an array');
  }
  const ids = new Set();
  for (const id of parsed) {
    ids.add(parseMissionId(id));
  }
  return ids;
}

/**
 * @param {string|number} missionId
 */
export function addDismissedWarEffortMissionId(missionId) {
  const n = parseMissionId(missionId);
  const ids = getDismissedWarEffortMissionIds();
  ids.add(n);
  const storage = requireSessionStorage();
  try {
    storage.setItem(WAR_EFFORT_DISMISSED_MISSION_IDS_KEY, JSON.stringify([...ids]));
  } catch (err) {
    throw new Error(`warEffortDismissedMissions: sessionStorage write failed (${err?.message ?? err})`);
  }
}

/**
 * @param {string|number} missionId
 * @returns {boolean}
 */
export function isWarEffortMissionDismissed(missionId) {
  if (missionId == null || missionId === '') {
    throw new Error('warEffortDismissedMissions: missionId is required');
  }
  return getDismissedWarEffortMissionIds().has(parseMissionId(missionId));
}

/**
 * True when this viewer already left or dismissed the mission (completion_seen_at set).
 * Used so FFA mission-update cannot restore a game the player abandoned.
 */
export function hasWarEffortViewerLeftMission(mission, userId) {
  if (!mission) {
    throw new Error('hasWarEffortViewerLeftMission: mission is required');
  }
  if (userId == null || userId === '') {
    return false;
  }
  const uid = Number(userId);
  if (!Number.isFinite(uid)) {
    throw new Error(`hasWarEffortViewerLeftMission: invalid userId ${userId}`);
  }
  let slot = null;
  if (mission.player1_id != null && Number(mission.player1_id) === uid) slot = 1;
  else if (mission.player2_id != null && Number(mission.player2_id) === uid) slot = 2;
  else if (mission.player3_id != null && Number(mission.player3_id) === uid) slot = 3;
  else if (mission.player4_id != null && Number(mission.player4_id) === uid) slot = 4;
  if (slot == null) return false;
  return mission[`player${slot}_completion_seen_at`] != null;
}

function missionOverMessageShownKey(gameId) {
  if (gameId == null || gameId === '') {
    throw new Error('warEffortDismissedMissions: gameId is required');
  }
  return `${WAR_EFFORT_MISSION_OVER_MESSAGE_SHOWN_PREFIX}${gameId}`;
}

/**
 * @param {string|number} gameId
 */
export function markWarEffortMissionOverMessageShown(gameId) {
  const storage = requireSessionStorage();
  try {
    storage.setItem(missionOverMessageShownKey(gameId), '1');
  } catch (err) {
    throw new Error(`warEffortDismissedMissions: sessionStorage write failed (${err?.message ?? err})`);
  }
}

/**
 * @param {string|number} gameId
 * @returns {boolean}
 */
export function hasWarEffortMissionOverMessageBeenShown(gameId) {
  const storage = requireSessionStorage();
  try {
    return storage.getItem(missionOverMessageShownKey(gameId)) === '1';
  } catch (err) {
    throw new Error(`warEffortDismissedMissions: sessionStorage read failed (${err?.message ?? err})`);
  }
}
